/**
 * 푸시 알림 켜기 (2026-10-02) — 구독 한 벌, 권한 시트, 권한 변화 듣기.
 *
 * - subscribePush(): 권한 요청 → 서비스 워커 → VAPID 구독 → 서버 저장. 권한 요청을 맨 먼저(동기로) 부르므로 누른 손의
 *   사용자 활성화가 살아 있을 때 창이 뜬다(아이폰 홈 화면 앱은 사용자 동작 안에서만 묻는다). 겹친 호출은 하나로 합친다.
 * - requestPush(opener): 벨의 「브라우저 알림 켜기」 · 폰 「전체」 시트의 「푸시 알림 켜기」가 누르는 길.
 *   아직 묻지 않았으면 시트(#pushSheet, partials/push-sheet.html)가 먼저 설명하고 「계속」에서 브라우저 창을 띄운다
 *   (실수로 「차단」을 누르는 일을 줄인다). 거부된 기기에는 그 기기에서 켜는 곳을 보인다 — 브라우저는 거부된 사이트에
 *   다시 묻지 않으므로(requestPermission 이 창 없이 denied) 앱 안에서 「다시 요청」할 길은 없다.
 * - watchPushPermission(): 기기 설정 · 사이트 정보에서 바꾸고 돌아오면(권한 변화 이벤트 · 화면 복귀) 다시 보고, 허용으로
 *   바뀌었으면 조용히 구독을 잇고 알린다. 시트가 열려 있으면 모양도 따라간다(거부 → 묻기로 되돌렸으면 「계속」).
 *   안드로이드 설치 앱은 예외다: 크롬이 앱 정보의 알림 설정을 앱이 시작할 때만 읽으므로 돌아와도 denied 그대로다
 *   (push-permission-logic.js relaunchHint) — 시트가 앱을 다시 열라고 하고, 켜는 방법을 본 기기(localStorage
 *   push.guided)는 다음에 열린 화면이 구독을 이으면서 「켰습니다」를 한 번 알린다.
 * - setupPushQuick(): 「전체」 시트 항목 · 구독 자동 복구 · 위의 듣기를 한 번 묶는다.
 * 화면이 다시 그려야 할 때는 document 의 `push:changed` 이벤트를 쏜다(벨 메뉴 단추가 듣는다).
 * 판정은 DOM 없는 push-permission-logic.js.
 */
import { showNotification } from './notifications.js';
import { openSheet, closeSheet } from './mobile-shell.js';
import { isIOS } from './pwa-install-logic.js';
import { pushHelpCase, pushUIState, pushSheetMode, permissionTurnedOn, relaunchHint } from './push-permission-logic.js';

function csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    if (meta && meta.content) return meta.content;
    const m = document.cookie.match(/(?:^|;\s*)_csrf=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : '';
}

function api(path, opts = {}) {
    opts.headers = Object.assign({ 'X-CSRF-Token': csrfToken() }, opts.headers || {});
    return fetch(path, opts).then((r) => {
        if (!r.ok) {
            const error = new Error('http ' + r.status);
            error.status = r.status;
            throw error;
        }
        return r.json();
    });
}

function urlB64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
    return arr;
}

export const pushSupported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
const permission = () => ('Notification' in window ? Notification.permission : 'default');

// 설치한 앱(홈 화면 앱 · 데스크톱 앱 창 · 그 창의 탭 틀 안)인가 — index.html 머리 스크립트의 판정과 같다.
function standaloneNow() {
    const h = document.documentElement;
    return h.classList.contains('is-standalone') || h.hasAttribute('data-workspace-frame') || navigator.standalone === true
        || !!(window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
}
const iosBrowser = () => isIOS(navigator.userAgent, navigator.maxTouchPoints) && !standaloneNow();

function helpCase() {
    return pushHelpCase({
        ua: navigator.userAgent,
        maxTouchPoints: navigator.maxTouchPoints,
        standalone: standaloneNow(),
        mobile: !!(navigator.userAgentData && navigator.userAgentData.mobile),
    });
}

// 지금 상태(push-permission-logic.js pushUIState) — 허용일 때만 구독을 물어본다.
export function readPushState() {
    const base = { supported: pushSupported(), iosBrowser: iosBrowser(), permission: permission() };
    if (!base.supported || base.iosBrowser || base.permission !== 'granted') return Promise.resolve(pushUIState(base));
    return navigator.serviceWorker.getRegistration('/')
        .then((reg) => (reg ? reg.pushManager.getSubscription() : null))
        .then((sub) => pushUIState(Object.assign(base, { subscribed: !!sub })))
        .catch(() => pushUIState(base));
}

// 권한 요청 — 옛 사파리의 콜백 모양도 받는다. 이미 허용이면 묻지 않는다.
function askPermission() {
    if (Notification.permission === 'granted') return Promise.resolve('granted');
    return new Promise((resolve) => {
        const p = Notification.requestPermission(resolve);
        if (p && typeof p.then === 'function') p.then(resolve, () => resolve(Notification.permission));
    });
}

let inflight = null;

// 구독 한 벌 — 벨 · 「전체」 시트 · 권한 시트의 「계속」 · 자동 복구 · 관리자 푸시 테스트가 같이 쓴다.
// 허용이 아니면 `permission`('denied' | 'default')을 실은 오류로 끝난다.
export function subscribePush() {
    if (inflight) return inflight;
    const asked = askPermission(); // 사용자 활성화가 살아 있을 때 — await 앞에서
    inflight = asked
        .then((perm) => {
            if (perm !== 'granted') {
                const error = new Error('permission ' + perm);
                error.permission = perm;
                throw error;
            }
            return navigator.serviceWorker.register('/sw.js', { scope: '/' });
        })
        .then(() => navigator.serviceWorker.ready)
        .then((reg) => api('/api/push/key').then((d) => reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlB64ToUint8Array(d.key),
        })))
        .then(postSubscription)
        .finally(() => { inflight = null; });
    return inflight;
}

export function postSubscription(sub) {
    return api('/api/push/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sub.toJSON()),
    });
}

function emitChanged() {
    document.dispatchEvent(new CustomEvent('push:changed'));
}

function labels() {
    return document.getElementById('pwaLabels')?.dataset || {};
}

function toast(text, type) {
    if (text) showNotification(text, type);
}

// 이 기기가 켜는 방법을 봤는가 — 앱을 다시 연 화면이 구독을 이으면 「켰습니다」를 한 번 알리려고(앱을 닫으면
// sessionStorage 는 사라지므로 localStorage).
const GUIDED = 'push.guided';
function pushGuided(v) {
    try {
        if (v === undefined) return localStorage.getItem(GUIDED) === '1';
        if (v) localStorage.setItem(GUIDED, '1'); else localStorage.removeItem(GUIDED);
    } catch (_) { /* 저장소가 막힌 창 — 알림 한 번을 놓칠 뿐 */ }
    return false;
}

// 한 번의 구독 시도에는 한 번만 알린다. 같은 시도를 기다리는 길이 여럿일 수 있다 — 돌아와서 허용을 본 감시가 구독을
// 시작한 사이(폰의 진짜 구독은 1초를 넘긴다) 아직 열린 시트의 「다시 확인」을 누르면 둘 다 같은 inflight 를 받는다
// (2026-10-02 기기에서 「등록 토스트 2개」). attempt 는 subscribePush() 가 돌려준 약속 — 없으면(이미 있던 구독을 다시
// 알렸을 때) 그대로 한 번 알린다.
let announced = null;
function announceOnce(attempt, ok) {
    if (attempt && announced === attempt) return;
    announced = attempt;
    if (ok) {
        pushGuided(false);
        toast(labels().pushEnabled || '푸시 알림을 켰습니다', 'success');
    } else {
        toast(labels().pushFailed || '푸시 알림을 켜지 못했습니다', 'error');
    }
}

// ── 권한 시트 ──
function pushSheet() {
    return document.getElementById('pushSheet');
}

function setStatus(sheet, text) {
    const el = sheet.querySelector('[data-push-status]');
    if (el) el.textContent = text || '';
}

function setBusy(sheet, busy) {
    sheet.querySelectorAll('[data-push-ask], [data-push-recheck]').forEach((b) => { b.disabled = busy; });
    if (busy) sheet.setAttribute('aria-busy', 'true'); else sheet.removeAttribute('aria-busy');
}

function paintSheet(sheet, mode) {
    const kase = helpCase();
    sheet.dataset.mode = mode;
    sheet.dataset.case = kase;
    sheet.querySelectorAll('[data-push-mode]').forEach((el) => { el.hidden = el.dataset.pushMode !== mode; });
    // data-push-case 는 경우 하나 또는 공백으로 이은 여럿(안내 줄을 여러 경우가 같이 쓴다)
    sheet.querySelectorAll('[data-push-case]').forEach((el) => { el.hidden = !el.dataset.pushCase.split(' ').includes(kase); });
    if (mode === 'blocked') pushGuided(true);
    sheet.setAttribute('aria-labelledby', mode === 'blocked' ? 'pushSheetBlockedTitle' : 'pushSheetAskTitle');
    setStatus(sheet, '');
    setBusy(sheet, false);
}

// 모양이 바뀌면 그 모양의 주 단추로 — 누른 「계속」이 숨으면 포커스가 body 로 떨어진다.
function focusPrimary(sheet) {
    const btn = sheet.querySelector(sheet.dataset.mode === 'blocked' ? '[data-push-recheck]' : '[data-push-ask]');
    if (btn && !sheet.hidden) btn.focus({ preventScroll: true });
}

function openPushSheet(opener, mode) {
    const sheet = pushSheet();
    if (!sheet) return false;
    paintSheet(sheet, mode);
    // 「전체」 시트 안에서 열면 「전체」를 먼저 닫는다(설치 시트와 같은 이유 — inert 와 포커스 복귀).
    const all = opener && opener.closest && opener.closest('#mAllSheet');
    if (all && !all.hidden) {
        closeSheet(all);
        opener = document.getElementById('mAllBtn') || opener;
    }
    if (sheet.hidden) openSheet(sheet, { opener });
    return true;
}

let lastPerm = null;

function enable(sheet) {
    if (sheet) setBusy(sheet, true);
    const attempt = subscribePush();
    return attempt
        .then(() => {
            if (sheet && !sheet.hidden) closeSheet(sheet, true);
            announceOnce(attempt, true);
            return true;
        })
        .catch((error) => {
            const perm = error && error.permission;
            if (perm === 'denied') {
                if (sheet && !sheet.hidden) { paintSheet(sheet, 'blocked'); focusPrimary(sheet); } else toast(labels().pushBlocked, 'error');
            } else if (perm) {
                // 창을 닫았다(default) — 다시 누르면 다시 묻는다
                if (sheet && !sheet.hidden) setStatus(sheet, sheet.dataset.lDismissed);
            } else {
                if (sheet && !sheet.hidden) setStatus(sheet, sheet.dataset.lFailed);
                announceOnce(attempt, false);
            }
            return false;
        })
        .finally(() => {
            if (sheet) setBusy(sheet, false);
            lastPerm = permission();
            emitChanged();
        });
}

// 누르는 길: 아직 묻지 않았으면 설명 → 「계속」, 거부면 켜는 방법, 이미 허용이면 곧바로 구독.
export function requestPush(opener) {
    if (!pushSupported()) return Promise.resolve(false);
    const mode = pushSheetMode(permission());
    if (mode && openPushSheet(opener, mode)) return Promise.resolve(true);
    if (mode === 'blocked') { toast(labels().pushBlocked, 'error'); return Promise.resolve(false); }
    return enable(null); // 시트가 없는 화면이거나 이미 허용
}

function recheck(sheet) {
    const now = permission();
    if (now === 'granted') { lastPerm = now; enable(sheet); return; }
    if (now === 'denied') {
        // 안드로이드 설치 앱은 다시 열어야 바뀐 설정이 보인다 — 「설정에서 바꾸세요」만 말하면 이미 바꾼 사람은 길을 잃는다
        const hint = relaunchHint(helpCase());
        const text = hint === 'required' ? sheet.dataset.lStillBlockedRelaunch : hint === 'maybe' ? sheet.dataset.lStillBlockedMaybe : '';
        setStatus(sheet, text || sheet.dataset.lStillBlocked);
        return;
    }
    // 기기 · 브라우저에서 「묻기」로 되돌렸다 — 이제 다시 물어볼 수 있다
    lastPerm = now;
    paintSheet(sheet, 'ask');
    setStatus(sheet, sheet.dataset.lCanAsk);
    focusPrimary(sheet);
    emitChanged();
}

// 조용히 구독을 이어도 되는 화면: 로그인한 화면(벨이 있다), 대리 보기가 아님(모든 쓰기를 거절한다).
function autoAllowed() {
    return !!document.getElementById('notifBell') && !document.querySelector('.impersonation-banner');
}

function onPermissionMaybeChanged() {
    if (!('Notification' in window)) return;
    const now = Notification.permission;
    if (now === lastPerm) return;
    const prev = lastPerm;
    lastPerm = now;
    emitChanged();
    const sheet = pushSheet();
    const open = !!sheet && !sheet.hidden;
    if (inflight) return; // 「계속」이 띄운 창의 답 — enable() 이 마무리한다
    if (permissionTurnedOn(prev, now)) {
        if (!pushSupported() || !autoAllowed()) { if (open) closeSheet(sheet, true); return; }
        const attempt = subscribePush();
        attempt
            .then(() => {
                if (open && !sheet.hidden) closeSheet(sheet, true);
                announceOnce(attempt, true);
            })
            .catch(() => {
                // 권한은 허용인데 구독이 안 됐다 — 조용히 넘기면 「아무 반응 없음」이다
                if (open && !sheet.hidden) setStatus(sheet, sheet.dataset.lFailed);
                announceOnce(attempt, false);
            })
            .finally(emitChanged);
        return;
    }
    if (!open) return;
    if (now === 'denied' && sheet.dataset.mode !== 'blocked') { paintSheet(sheet, 'blocked'); focusPrimary(sheet); }
    if (now !== 'denied' && sheet.dataset.mode === 'blocked') { paintSheet(sheet, 'ask'); setStatus(sheet, sheet.dataset.lCanAsk); focusPrimary(sheet); }
}

let watching = false;
export function watchPushPermission() {
    if (watching || !('Notification' in window)) return;
    watching = true;
    lastPerm = Notification.permission;
    try {
        const q = navigator.permissions && navigator.permissions.query({ name: 'notifications' });
        if (q && typeof q.then === 'function') q.then((st) => st.addEventListener('change', onPermissionMaybeChanged)).catch(() => {});
    } catch (_) { /* 권한 API 가 없는 브라우저 — 화면 복귀로만 본다 */ }
    // 기기 설정에서 돌아오면 화면이 다시 보인다
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') onPermissionMaybeChanged(); });
    window.addEventListener('focus', onPermissionMaybeChanged);
    window.addEventListener('pageshow', onPermissionMaybeChanged);
}

// ── 폰 「전체」 시트의 항목 ──
// 아직 켜지 않았으면 「푸시 알림 켜기」, 거부면 「알림 켜는 방법」. 지원하지 않거나 이미 켰거나 아이폰 브라우저 탭이면 숨긴다.
function syncPushItems() {
    const items = document.querySelectorAll('[data-pwa-push]');
    if (!items.length) return;
    readPushState().then((st) => items.forEach((el) => {
        el.hidden = st !== 'off' && st !== 'blocked';
        el.dataset.pushState = st;
        el.querySelectorAll('[data-push-label]').forEach((span) => { span.hidden = span.dataset.pushLabel !== (st === 'blocked' ? 'help' : 'on'); });
    }));
}

// 이 탭에서 이미 서버에 구독을 알렸는가 (세션마다 한 번). 로그인 화면(벨 없음)이 지워 다음 사용자가 다시 알린다.
const PUSH_SYNCED = 'pwa.pushSynced';
function pushSynced(v) {
    try {
        if (v === undefined) return sessionStorage.getItem(PUSH_SYNCED) === '1';
        if (v) sessionStorage.setItem(PUSH_SYNCED, '1'); else sessionStorage.removeItem(PUSH_SYNCED);
    } catch (e) { /* 저장소를 못 쓰면 매번 확인만 한다 */ }
    return false;
}

let quickWired = false;
export function setupPushQuick() {
    if (quickWired) return;
    quickWired = true;
    document.addEventListener('click', (e) => {
        const item = e.target.closest('[data-pwa-push]');
        if (item) {
            e.preventDefault();
            requestPush(item);
            return;
        }
        const sheet = e.target.closest('#pushSheet');
        if (!sheet) return;
        if (e.target.closest('[data-push-ask]')) { e.preventDefault(); enable(sheet); return; }
        if (e.target.closest('[data-push-recheck]')) { e.preventDefault(); recheck(sheet); }
    });
    ['calendar:navigated', 'calendar:page-swapped', 'push:changed'].forEach((name) => document.addEventListener(name, syncPushItems));
    document.addEventListener('click', (e) => { if (e.target.closest('#mAllBtn')) syncPushItems(); }, true);
    syncPushItems();
    watchPushPermission();
    // 구독 자동 복구 (spec §4.1): 로그인한 화면(벨이 있는 화면)에서만. 로그아웃해 벨이 없는 화면에 오면 이 탭의
    // "알렸음" 표시를 지운다 — 같은 기기의 다음 사용자가 구독을 자기 것으로 다시 묶는다.
    if (!document.getElementById('notifBell')) { pushSynced(false); return; }
    // 대리 보기는 모든 쓰기를 거절하므로(impersonationBlocked) 여기서 구독하면 브라우저에만 남는다.
    if (document.querySelector('.impersonation-banner')) return;
    if (pushSupported() && Notification.permission === 'granted') {
        // 켜는 방법을 본 기기가 (앱을 다시 열어) 허용으로 돌아왔다 — 이어지면 한 번 알린다
        const guided = pushGuided();
        let attempt = null;
        navigator.serviceWorker.ready
            .then((reg) => reg.pushManager.getSubscription())
            .then((sub) => {
                // 권한은 허용인데 구독이 없다(브라우저가 만료·교체) → 조용히 다시 구독한다.
                if (!sub) {
                    attempt = subscribePush();
                    return attempt.then(() => pushSynced(true));
                }
                // 구독은 있는데 서버가 모를 수 있다(대리 보기 중 거절된 등록, 같은 기기의 다른 사용자, 서버 정리) —
                // 세션마다 한 번 다시 알린다; 서버는 같은 endpoint 를 지금 사용자에게 다시 묶는다 (리뷰 #4).
                if (pushSynced()) return null;
                return postSubscription(sub).then(() => pushSynced(true));
            })
            .then(() => { if (guided) announceOnce(attempt, true); })
            .then(syncPushItems)
            .catch(() => { if (guided) { pushGuided(false); announceOnce(attempt, false); } });
    }
}
